import * as React from "react";
import moment from "moment";

import {
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    FormControlLabel,
    TextField,
    Divider,
} from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

// Enum values (match your C# numeric values)
const PostingScheduleType = {
    ALL: 0,
    N_VIDEO_COUNT: 1,
    N_DAYS: 2,
};

const PostingScheduleTypeLabel = {
    0: "ALL (Every video they post)",
    1: "N_VIDEO_COUNT (Every N videos)",
    2: "N_DAYS (Every N days)",
};

// ---- Helpers ----
function getRequirePostingSchedule(p) {
    return Boolean(p?.require_posting_schedule);
}

function getPostingScheduleConfig(p) {
    const cfg = p?.posting_schedule_config || {};
    const scheduleType =
        cfg.schedule_type === 0 || cfg.schedule_type === 1 || cfg.schedule_type === 2
            ? cfg.schedule_type
            : PostingScheduleType.ALL;

    return {
        schedule_type: scheduleType,
        threshold: Number.isFinite(cfg.threshold) ? cfg.threshold : 0,
    };
}

function getStartDateMoment(p) {
    const raw = p?.posting_schedule_start_date ?? null;
    if (!raw) return null;

    const m = moment(raw);
    return m.isValid() ? m : null;
}

function clampInt(v, min) {
    if (!Number.isFinite(v)) return min;
    return Math.max(min, Math.floor(v));
}

// ---- Component ----
export function PostingScheduleSettings({ existingProject, setExistingProject }) {
    const requirePostingSchedule = getRequirePostingSchedule(existingProject);
    const config = getPostingScheduleConfig(existingProject);
    const startDate = getStartDateMoment(existingProject);

    const isNType =
        config.schedule_type === PostingScheduleType.N_VIDEO_COUNT ||
        config.schedule_type === PostingScheduleType.N_DAYS;

    const patchExistingProject = (patch) => {
        setExistingProject((prev) => ({ ...prev, ...patch }));
    };

    const patchPostingScheduleConfig = (patch) => {
        setExistingProject((prev) => {
            const prevCfg = getPostingScheduleConfig(prev);
            return {
                ...prev,
                posting_schedule_config: {
                    ...prevCfg,
                    ...patch,
                },
            };
        });
    };

    const minDate = moment().startOf("day");

    // Ensure stored start date is not in the past; if it is, clear it.
    React.useEffect(() => {
        console.log(existingProject);
        if (!existingProject?.posting_schedule_start_date) return;
        const m = getStartDateMoment(existingProject);
        if (m && m.isBefore(minDate, "day")) {
            patchExistingProject({ posting_schedule_start_date: null });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6">Posting Schedule</Typography>

            <FormControlLabel
                control={
                    <Switch
                        checked={requirePostingSchedule}
                        onChange={(e) =>
                            patchExistingProject({ require_posting_schedule: e.target.checked })
                        }
                    />
                }
                label="Require posting schedule"
            />

            <Divider />

            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    opacity: requirePostingSchedule ? 1 : 0.6,
                }}
            >
                <FormControl fullWidth disabled={!requirePostingSchedule}>
                    <InputLabel id="posting-schedule-type-label">Schedule type</InputLabel>
                    <Select
                        labelId="posting-schedule-type-label"
                        label="Schedule type"
                        value={config.schedule_type}
                        onChange={(e) => {
                            const nextType = Number(e.target.value);

                            if (nextType === PostingScheduleType.ALL) {
                                // For ALL: threshold not applicable; set to 0
                                patchPostingScheduleConfig({ schedule_type: nextType, threshold: 0 });
                                return;
                            }

                            // For N_*: threshold is REQUIRED, ensure >= 1
                            const nextThreshold = clampInt(config.threshold || 1, 1);
                            patchPostingScheduleConfig({
                                schedule_type: nextType,
                                threshold: nextThreshold,
                            });
                        }}
                    >
                        <MenuItem value={PostingScheduleType.ALL}>
                            {PostingScheduleTypeLabel[PostingScheduleType.ALL]}
                        </MenuItem>
                        <MenuItem value={PostingScheduleType.N_VIDEO_COUNT}>
                            {PostingScheduleTypeLabel[PostingScheduleType.N_VIDEO_COUNT]}
                        </MenuItem>
                        <MenuItem value={PostingScheduleType.N_DAYS}>
                            {PostingScheduleTypeLabel[PostingScheduleType.N_DAYS]}
                        </MenuItem>
                    </Select>
                </FormControl>

                {isNType && (
                    <TextField
                        type="number"
                        required
                        label={
                            config.schedule_type === PostingScheduleType.N_VIDEO_COUNT
                                ? "Every N videos"
                                : "Every N days"
                        }
                        value={config.threshold >= 1 ? config.threshold : ""}
                        onChange={(e) => {
                            const v = Number(e.target.value);
                            // allow temporary empty while typing
                            if (e.target.value === "") {
                                patchPostingScheduleConfig({ threshold: 0 });
                                return;
                            }
                            patchPostingScheduleConfig({ threshold: clampInt(v, 1) });
                        }}
                        onBlur={() => {
                            // enforce required >=1 when leaving the field
                            const enforced = clampInt(config.threshold || 1, 1);
                            if (enforced !== config.threshold) {
                                patchPostingScheduleConfig({ threshold: enforced });
                            }
                        }}
                        disabled={!requirePostingSchedule}
                        inputProps={{ min: 1, step: 1 }}
                        error={requirePostingSchedule && config.threshold < 1}
                        helperText={
                            requirePostingSchedule && config.threshold < 1
                                ? "Threshold is required (must be at least 1)."
                                : " "
                        }
                        sx={{ maxWidth: 280 }}
                    />
                )}

                <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DatePicker
                        label="Start"
                        value={startDate}              // ✅ controlled
                        format="DD/MM/YYYY"
                        minDate={minDate}
                        onChange={(value) => {
                            if (!value || !value.isValid()) {
                                patchExistingProject({ posting_schedule_start_date: null });
                                return;
                            }
                            if (value.isBefore(minDate, "day")) {
                                patchExistingProject({ posting_schedule_start_date: null });
                                return;
                            }
                            patchExistingProject({ posting_schedule_start_date: value.toISOString() });
                        }}
                        disabled={!requirePostingSchedule}
                    />

                </LocalizationProvider>
            </Box>
        </Box>
    );
}

// ---- Example usage ----
export default function ExamplePage() {
    const [existingProject, setExistingProject] = React.useState({
        project_brands: [],
        project_channels: [],
        require_posting_schedule: false,
        posting_schedule_config: { schedule_type: 0, threshold: 0 },
        posting_schedule_start_date: null,
    });

    return (
        <PostingScheduleSettings
            existingProject={existingProject}
            setExistingProject={setExistingProject}
        />
    );
}
